/*
 *   File : emailTemplate.js
 *   Description : Shared helpers for email notification banner rendering.
 *                 Used by compose preview, history modal, and HTML expand view
 *                 so inline banner markup stays consistent.
 *   Version : v1.0
 */

const escapeHtmlAttr = (value) =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

/**
 * buildEmailBannerHtml
 * Returns email-client-safe inline banner markup for a public image URL.
 * Returns an empty string when imageUrl is missing.
 */
export const buildEmailBannerHtml = (imageUrl) => {
    const url = (imageUrl || "").trim();
    if (!url) return "";

    const safeUrl = escapeHtmlAttr(url);

    return `<div style="width:100%;">
  <img
    src="${safeUrl}"
    alt="Email Banner"
    style="display:block;width:100%;max-width:100%;height:auto;border:0;"
  />
</div>`;
};

/**
 * prependEmailBanner
 * Inserts the inline banner before existing HTML content (BODY_CONTENT or HTML_CONTENT).
 */
export const prependEmailBanner = (content, imageUrl) => {
    const banner = buildEmailBannerHtml(imageUrl);
    if (!banner) return content || "";
    return `${banner}\n${content || ""}`;
};

/**
 * prepareHtmlForPreview
 * Normalizes uploaded HTML for in-app preview: extracts <body> from full documents
 * so nested html/body tags and document-level widths do not break the mockup.
 */
export const prepareHtmlForPreview = (html) => {
    const raw = (html || "").trim();
    if (!raw) return "";

    if (/<html[\s>]/i.test(raw) || /<body[\s>]/i.test(raw)) {
        try {
            const doc = new DOMParser().parseFromString(raw, "text/html");
            const bodyHtml = doc.body?.innerHTML?.trim();
            if (bodyHtml) return bodyHtml;
        } catch (e) {
            // fall through to raw html
        }
    }

    return raw;
};

/**
 * getEmailImageUrlFromData
 * Resolves image_url from saved/history email_data objects.
 */
export const getEmailImageUrlFromData = (emailData) => {
    if (!emailData) return "";
    return emailData.image_url || "";
};
